import { useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../components/ui'
import { formatPlanDisplay } from '../services/planDisplay'
import { isPersonalMode } from '../utils/cockpitMode'
import InvoicesSection from '../components/InvoicesSection'
import TokenHistory from './documents/TokenHistory'
import TokenAccountCard from '../components/TokenAccountCard'
import OpenInvoices from '../components/OpenInvoices'
import { forbiddenText } from '../lib/forbiddenText'
import './Services.css'

interface Organization {
  id: number
  name: string
  slug: string
    public_token?: string
  member_role: string
}

interface OrgService {
  id: number
  erpnext_subscription_id: string
  plan_id: string
  plan_name: string | null
  // TQ #5466: Display nutzt item_name + variant_name (siehe planDisplay.ts).
  item_name?: string | null
  variant_name?: string | null
  status: string
  billing_interval: string
  rate: number
  currency: string
  start_date: string | null
  current_invoice_end: string | null
  item_group: string | null
  // gesetzt → Subscription wird über Stripe abgerechnet (Lastschrift via Mandate)
  // NULL → Banküberweisung. Source-of-Truth siehe Billing-Modi.md.
  stripe_subscription_id: string | null
}

interface SubscriptionGroup {
  subscriptionId: string
  startDate: string | null
  currentInvoiceEnd: string | null
  currency: string
  plans: OrgService[]
  totalMonthly: number
  // true wenn die Sub eine Stripe-Subscription hat (auto-debit)
  isStripeManaged: boolean
}

interface Props {
  organization: Organization
}

// ERPNext liefert billing_interval als "Year"/"Month"/"Quarter"/"Half-Yearly"
// (Capitalized, wie im ERPNext-DocType). Lowercase-Aliase ("yearly", "quarterly")
// stehen drin für historische Plan-Daten. Normalisierung hier — Source of Truth
// ist die rate pro Periode, wir teilen runter auf monatlich.
const monthlyRate = (s: OrgService) => {
  switch (s.billing_interval?.toLowerCase()) {
    case 'year':
    case 'yearly':       return s.rate / 12
    case 'half-yearly':  return s.rate / 6
    case 'quarter':
    case 'quarterly':    return s.rate / 3
    case 'month':
    case 'monthly':
    default:             return s.rate
  }
}

// Deutsches Label für die Abrechnungs-Periode (aus billing_interval der Plans).
// Wenn Plans innerhalb derselben Sub unterschiedliche Intervalle haben (sollte
// laut ERPNext nicht passieren, ein Subscription = ein Intervall), nehmen wir
// das des ersten Plans als Repräsentant.
const intervalLabel = (plans: OrgService[]): string => {
  const iv = plans[0]?.billing_interval?.toLowerCase()
  switch (iv) {
    case 'year':
    case 'yearly':       return 'jährlich'
    case 'half-yearly':  return 'halbjährlich'
    case 'quarter':
    case 'quarterly':    return 'quartalsweise'
    case 'month':
    case 'monthly':      return 'monatlich'
    default:             return iv || 'unbekannt'
  }
}

// Kurzform der Periode für Inline-Anzeige neben Beträgen ("/Mo", "/Jahr", ...).
const intervalSuffix = (iv?: string): string => {
  switch (iv?.toLowerCase()) {
    case 'year':
    case 'yearly':       return '/Jahr'
    case 'half-yearly':  return '/Halbjahr'
    case 'quarter':
    case 'quarterly':    return '/Quartal'
    case 'month':
    case 'monthly':
    default:             return '/Mo'
  }
}

// Gruppen-Gesamtbetrag (TQ #6575): bei einheitlichem NICHT-monatlichem Intervall
// (z.B. G-DATA Jahreslizenz) im NATIVEN Intervall anzeigen ("33,57 EUR/Jahr")
// plus Monats-Vergleichswert. Bei monatlich ODER gemischten Intervallen wie
// bisher als reiner Monatswert. Vorher wurde alles auf /Monat runtergerechnet,
// was Jahreslizenzen als irrefuehrendes "≈2,80/Mo" zeigte.
const groupTotalLabel = (group: SubscriptionGroup): string => {
  const ivs = group.plans.map(p => p.billing_interval?.toLowerCase() || 'month')
  const allSame = ivs.every(i => i === ivs[0])
  const iv = ivs[0]
  const isMonthly = iv === 'month' || iv === 'monthly'
  if (allSame && !isMonthly) {
    const native = group.plans.reduce((a, p) => a + p.rate, 0)
    return `${native.toFixed(2)} ${group.currency}${intervalSuffix(iv)} · ≈ ${group.totalMonthly.toFixed(2)}/Mo`
  }
  return `≈ ${group.totalMonthly.toFixed(2)} ${group.currency} / Monat`
}

export default function Billing({ organization }: Props) {
  const { api: keycloakApi } = useAuth()
  const { showToast } = useToast()
  // TQ #6930: Personal-Mode (with.it-sce.de) hat keine Org → Daten kommen aus
  // /api/invoices + /api/billing-portal/session, org-only Sektionen werden hidden.
  const personal = isPersonalMode()

  const [services, setServices] = useState<OrgService[]>([])
  const [openingPortal, setOpeningPortal] = useState(false)

  // Personal-User ist faktisch Owner seiner eigenen Abrechnung → Portal erlaubt.
  const isAdminOrOwner = personal || organization.member_role === 'owner' || organization.member_role === 'admin'

  // Review-Fund 07.10.2026: "Token-Historie →"-Link im Dashboard verlinkt auf
  // "billing#token-verlauf" (seit fd64401, 03.10.2026), landet aber ohne
  // diesen Effect nur oben auf der Seite — der Abschnitt (id="token-verlauf")
  // ist weiter unten und wird nicht angesprungen. Gleiches Muster wie
  // Settings.tsx (Zeile ~638), dort fuer Query-Param-Tabs, hier fuer den
  // URL-Hash direkt.
  useEffect(() => {
    const hash = window.location.hash?.slice(1)
    if (!hash) return
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    // TQ #7069: Personal-Mode nutzt /api/me/services (schema-kompatibel zu
    // /api/org/<slug>/services, TQ #6906) — liefert die Subscriptions des
    // eingeloggten Users (subscriptions_cache JOIN customers via erpnext_customer_id).
    const url = personal
      ? '/api/me/services'
      : `/api/org/${organization.public_token || organization.slug}/services`
    keycloakApi!.get(url)
      .then(r => setServices(r.data?.services || []))
      .catch((e: unknown) => {
        setServices([])
        // TQ #960 (Patric 25.10.): ERP-403 auf Subscription still abfangen, Abo-Bereich bleibt leer, kein Toast.
        if (forbiddenText(e) === null) console.error('Failed to load services:', e)
      })
  }, [personal, (organization.public_token || organization.slug), keycloakApi])

  async function openBillingPortal() {
    setOpeningPortal(true)
    try {
      const portalEndpoint = personal
        ? '/api/billing-portal/session'
        : `/api/org/${organization.public_token || organization.slug}/billing/portal-session`
      const res = await keycloakApi!.post(portalEndpoint, {
        return_url: window.location.href,
      })
      const url = res.data?.portal_url
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        showToast('error', 'Billing Portal Link nicht erhalten')
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { error?: string } } }
      const status = err.response?.status
      const msg = err.response?.data?.error ?? 'Unbekannter Fehler'
      if (status === 422) {
        showToast('info', 'Diese Organisation hat noch keine Zahlung über Stripe getätigt. Erst einen Plan buchen.')
      } else {
        showToast('error', `Billing Portal konnte nicht geöffnet werden: ${msg}`)
      }
    } finally {
      setOpeningPortal(false)
    }
  }

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="page-header" style={{ order: 0 }}>
        <div>
          <h1>Abrechnung</h1>
          <p className="page-subtitle">
            {personal
              ? `Rechnungen + Zahlungsmittel für ${organization.name}`
              : `Token-Verlauf und Zahlungsmittel für ${organization.name} — Rechnungen finden Sie unter Dokumente`}
          </p>
        </div>
      </div>

      {/* Laufende Subscriptions — unten via order=3 (Detail-Anzeige, weniger interaktiv als Rechnungen/Zahlmittel) */}
      {services.length > 0 && (() => {
        const groups: SubscriptionGroup[] = Object.values(
          services.reduce((acc, s) => {
            const key = s.erpnext_subscription_id
            if (!acc[key]) acc[key] = { subscriptionId: key, startDate: s.start_date, currentInvoiceEnd: s.current_invoice_end ?? null, currency: s.currency || 'EUR', plans: [], totalMonthly: 0, isStripeManaged: !!s.stripe_subscription_id }
            acc[key].plans.push(s)
            acc[key].totalMonthly += monthlyRate(s)
            return acc
          }, {} as Record<string, SubscriptionGroup>)
        )
        return (
          <section className="settings-section" style={{ marginBottom: '1.5rem', order: 3 }}>
            <div className="section-header">
              <h2>📋 Laufende Subscriptions</h2>
              <span className="section-badge">{groups.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {groups.map(group => (
                <div key={group.subscriptionId} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                  {/* Sub-Header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.625rem 0.875rem', background: '#f8fafc', borderBottom: '1px solid #e5e7eb',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                      <span style={{
                        padding: '2px 7px', borderRadius: 999, fontSize: '0.72rem',
                        fontWeight: 600, background: '#d1fae5', color: '#065f46',
                      }}>● Aktiv</span>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>{group.plans.map(formatPlanDisplay).join(', ')}</span>
                      <code style={{ fontSize: '0.8rem', color: '#6b7280', background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>
                        {group.subscriptionId}
                      </code>
                      {/* Bezahlmodus-Badge (Source-of-Truth: stripe_subscription_id-Präsenz) */}
                      {group.isStripeManaged ? (
                        <span style={{
                          padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem',
                          fontWeight: 600, background: '#ede9fe', color: '#5b21b6',
                        }} title="Stripe zieht Lastschrift automatisch ein">💳 Lastschrift via Stripe</span>
                      ) : (
                        <span style={{
                          padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem',
                          fontWeight: 600, background: '#fef3c7', color: '#92400e',
                        }} title="Kunde überweist auf IT-SCE-IBAN">🏦 Banküberweisung</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {group.currentInvoiceEnd && (
                        <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 500 }}>
                          bezahlt bis {new Date(group.currentInvoiceEnd).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </span>
                      )}
                      {group.startDate && !group.currentInvoiceEnd && (
                        <span style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>
                          seit {new Date(group.startDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Footer: Gesamt im nativen Abrechnungs-Intervall (Jahres-Subs
                      wie G-DATA als /Jahr, mit Monats-Vergleichswert) — TQ #6575 */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.5rem 0.875rem', borderTop: '2px solid #e5e7eb', background: '#f8fafc',
                  }}>
                    <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{intervalLabel(group.plans)} abgerechnet · Details unter Services</span>
                    <span style={{ fontWeight: 700, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }}>
                      {groupTotalLabel(group)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )
      })()}

      {/* Kontostand-Kopf und offene Rechnungen (Spec Informationsarchitektur, PR 2): Konto und Zahlung
          an einer Stelle. Kontostand = dieselbe Quelle wie das Dashboard (/erpnext/quota), offene
          Rechnungen = ERP-Live-Liste (/erpnext/invoices), Belege selbst bleiben in Dokumente. */}
      {!personal && (
        <div style={{ order: 0, marginBottom: '1.5rem' }}>
          <TokenAccountCard slug={organization.public_token || organization.slug} />
        </div>
      )}
      {!personal && <OpenInvoices slugOrToken={organization.public_token || organization.slug} />}

      {/* Token-Verlauf (Spec Informationsarchitektur, Patric 03.10.2026): das Buchungsjournal des
          Token-Kontos gehoert zur Abrechnung, nicht in die Dokumente. Quelle unveraendert
          GET /documents/token-history (Owner/Admin). Davor das Dashboard mit Kontostand-Kurzzahl und Link. */}
      {!personal && isAdminOrOwner && (
        <section id="token-verlauf" className="settings-section" style={{ marginBottom: '1.5rem', order: 1 }}>
          <div className="section-header">
            <h2>🪙 Token-Verlauf</h2>
          </div>
          <TokenHistory organization={organization} />
        </section>
      )}

      {/* Zahlungsmittel-Card — order=2 (zwischen Rechnungen oben und Subscriptions unten) */}
      <section className="settings-section" style={{ marginBottom: '1.5rem', order: 2 }}>
        <div className="section-header">
          <h2>💳 Zahlungsmittel & Stripe-Belege</h2>
        </div>
        <p style={{ margin: '0 0 0.75rem', color: '#6b7280', fontSize: '0.9rem' }}>
          Verwalten Sie Karte oder SEPA-Lastschrift, aktualisieren Sie
          Rechnungsadresse/USt-IdNr. und sehen Sie Ihre Karten-Belege.
        </p>
        {!personal && (
          <div style={{
            padding: '0.625rem 0.875rem',
            background: '#fef3c7',
            color: '#92400e',
            borderRadius: 6,
            fontSize: '0.8125rem',
            marginBottom: '0.875rem',
            lineHeight: 1.45,
          }}>
            <strong>Hinweis:</strong> Die im Stripe-Portal sichtbaren Belege sind
            <strong> Zahlungsquittungen</strong>, nicht die rechtlich verbindlichen Rechnungen.
            Die offiziellen, USt-konformen Rechnungen finden Sie unter
            „Dokumente" → „Rechnungen".
          </div>
        )}
        {isAdminOrOwner ? (
          <button
            type="button"
            onClick={openBillingPortal}
            disabled={openingPortal}
            title="Karten-Belege ergänzen die rechtlichen Rechnungen — sind aber keine USt-Rechnungen"
            style={{
              padding: '0.625rem 1.25rem',
              background: '#635bff', // Stripe-Lila
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: openingPortal ? 'wait' : 'pointer',
              fontSize: '0.875rem',
            }}
          >
            {openingPortal ? 'Wird geöffnet …' : 'Stripe Billing Portal öffnen ↗'}
          </button>
        ) : (
          <div style={{ padding: '0.75rem 1rem', background: '#f3f4f6', borderRadius: 6, color: '#6b7280', fontSize: '0.875rem' }}>
            Nur Inhaber und Administratoren können das Billing Portal öffnen.
          </div>
        )}
      </section>

      {personal && <InvoicesSection organization={organization} personal={personal} />}
    </div>
  )
}
